import { Badge, Box, Icon, styleLink } from "orcs-design-system";
import { isFinite } from "lodash";
import React from "react";
import styled, { css } from "styled-components";
import themeGet from "@styled-system/theme-get";
import { Link } from "react-router-dom";

import PropTypes from "prop-types";
import icons from "src/config/icons";
import GroupPropType from "src/custom-prop-types/group";
import { GroupTypesPropType } from "src/custom-prop-types/groupTypes";
import { getGroupBadgeVariant } from "src/util/group";
import { roundFte } from "src/util/roundingStrategy";
import { teamDetailsPath } from "src/util/routing";

import GroupHierarchyBreadcrumb from "../GroupHierarchyBreadcrumb";

const StyledRouterLink = styleLink(Link);

const StyledBadge = styled(Badge).withConfig({
  shouldForwardProp: (prop) =>
    !["$textColor", "$dashed", "$static"].includes(prop),
})`
  cursor: ${(p) => (p.$static ? "inherit" : "pointer")};
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  ${(p) =>
    p.$textColor &&
    css`
      color: ${themeGet(`colors.${p.$textColor}`)} !important;
    `}
  ${(p) =>
    p.$dashed &&
    css`
      border-style: dashed !important;
    `}
`;

const BadgeLink = styled(StyledRouterLink)`
  display: block;
  max-width: 100%;
  min-width: 0;
  text-decoration: none !important;
  color: inherit !important;
  font-weight: inherit !important;
  font-size: inherit !important;
  &:hover {
    text-decoration: none !important;
    color: inherit !important;
  }
  &:focus {
    text-decoration: none !important;
    color: inherit !important;
  }
  &:visited {
    color: inherit !important;
  }
`;

const BadgeLabel = styled(Box)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CustomIcon = styled(Icon)`
  flex-shrink: 0;
  line-height: 1;
  padding-right: 4px;

  svg {
    opacity: 0.5;
    transition: opacity 300ms ease-in-out;
    transition-delay: 250ms;
  }
  &:hover {
    svg {
      opacity: 1;
    }
  }
`;

const GroupBadge = ({
  group,
  groupTypes,
  showBreadcrumbIcon = true,
  disableBreadcrumb = false,
  disableLink = false,
  /** Stronger fill — e.g. pivot team on reporting-line cards. */
  emphasized = false,
  variant,
  altStyle,
  textColor,
  dashed = false,
  diff,
  fte,
  fteLabel,
  displayName,
  showGroupTypeInBreadcrumb = false,
  breadcrumbTabIndex,
}) => {
  const resolvedVariant = variant ?? getGroupBadgeVariant(group, groupTypes);
  const displayedFte = isFinite(fte) ? fte : group.allocatedFte;
  const badgeContent = (
    <StyledBadge
      noWrap
      variant={resolvedVariant}
      altStyle={altStyle ?? emphasized}
      $textColor={textColor}
      $dashed={dashed}
      $static={disableLink}
      diff={diff}
      data-testid={`group-badge-${group.id}`}
      data-variant={resolvedVariant}
      data-emphasized={emphasized ? "true" : undefined}
      data-dashed={dashed ? "true" : undefined}
      data-text-color={textColor}
      data-diff={diff}
    >
      {showBreadcrumbIcon && !disableBreadcrumb && (
        <CustomIcon icon={icons.groupBreadcrumb} size="s" />
      )}
      <BadgeLabel as="span" overflow="hidden" minWidth={0}>
        {displayName ?? group.name}
        {isFinite(displayedFte) &&
          displayedFte > 0 &&
          ` (${roundFte(displayedFte)} FTE${fteLabel ? ` ${fteLabel}` : ""})`}
      </BadgeLabel>
    </StyledBadge>
  );

  // Static badge: no router link, and the hover breadcrumb shows plain team
  // names instead of links. Used inside pickers / modals where a mis-click on
  // a badge would otherwise navigate away and drop the user's in-progress work.
  const handleClick = (e) => {
    // Stop propagation to prevent interfering with parent click handlers
    // (e.g., org chart card selection). This allows the badge to navigate
    // to the team page without triggering parent element clicks.
    e.stopPropagation();
  };

  const teamPath = teamDetailsPath(group);

  const linked = (
    <Box
      display="block"
      maxWidth="100%"
      minWidth={0}
      overflow="hidden"
      onClick={handleClick}
    >
      <BadgeLink to={teamPath}>{badgeContent}</BadgeLink>
    </Box>
  );

  let chip = badgeContent;
  if (disableLink) {
    if (!disableBreadcrumb) {
      chip = (
        <GroupHierarchyBreadcrumb
          group={group}
          groupTypes={groupTypes}
          showGroupTypeBadge={showGroupTypeInBreadcrumb}
          tabIndex={breadcrumbTabIndex}
          useChildren
          disableLinks
        >
          {badgeContent}
        </GroupHierarchyBreadcrumb>
      );
    }
  } else if (disableBreadcrumb) {
    chip = linked;
  } else {
    chip = (
      <GroupHierarchyBreadcrumb
        group={group}
        groupTypes={groupTypes}
        showGroupTypeBadge={showGroupTypeInBreadcrumb}
        tabIndex={breadcrumbTabIndex}
        useChildren
      >
        {linked}
      </GroupHierarchyBreadcrumb>
    );
  }

  // overflow:hidden + min-width:0 lets this chip shrink as a flex item so
  // nowrap labels cannot paint outside member rows or org-chart cards.
  return (
    <Box maxWidth="100%" minWidth={0} overflow="hidden">
      {chip}
    </Box>
  );
};

GroupBadge.propTypes = {
  group: GroupPropType.isRequired,
  groupTypes: GroupTypesPropType.isRequired,
  showBreadcrumbIcon: PropTypes.bool,
  /** Suppress the GroupHierarchyBreadcrumb hover popover — use inside popovers to avoid nesting. */
  disableBreadcrumb: PropTypes.bool,
  /** Non-navigating badge: no link; breadcrumb popover stays but shows plain names. */
  disableLink: PropTypes.bool,
  emphasized: PropTypes.bool,
  variant: PropTypes.string,
  altStyle: PropTypes.bool,
  textColor: PropTypes.string,
  dashed: PropTypes.bool,
  diff: PropTypes.oneOf(["added", "removed", "changed"]),
  /** Person-level capacity/allocation shown inside the team chip. */
  fte: PropTypes.number,
  /** Clarifies person-level FTE as capacity or allocated. */
  fteLabel: PropTypes.oneOf(["capacity", "allocated"]),
  /** Rendered team name, for example with search highlighting. */
  displayName: PropTypes.node,
  /** Include the team type alongside hierarchy in the hover content. */
  showGroupTypeInBreadcrumb: PropTypes.bool,
  /** Override hover-trigger tab order when the chip sits inside another link. */
  breadcrumbTabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default GroupBadge;
